<script setup lang="ts">
import { Head } from '@vueuse/head';
import ShowcaseItemSkeleton from '@/components/ShowcaseItemSkeleton.vue';
import SkeletonBox from '@/components/SkeletonBox.vue';
import { defineAsyncComponent } from 'vue';
import LazyLoadOnVisible from '@/components/LazyLoadOnVisible.vue';
import Lazy from '@/components/Lazy.vue';
import { PRODUCTION } from '@/env';

const MainSearchSection = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "MainSearchSection" */ '@/components/MainSearchSection.vue')
});
const MainBannerLoader = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "MainBannerLoader" */ '@/components/MainBannerLoader.vue')
});
const DestinationsSection = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "DestinationsSection" */ '@/components/DestinationsSection.vue')
});
const DelegateCruiseSection = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "DelegateCruiseSection" */ '@/components/DelegateCruiseSection.vue')
});
const Reviews = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "Reviews" */ '@/components/Reviews.vue')
});
const Footer = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "Footer" */ '@/components/Footer.vue')
});
const RecommendedCruises = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "RecommendedCruises" */ '@/components/RecommendedCruises.vue')
});
</script>
<template>
    <div>

        <Head>
            <title>{{ $t('TITLES.homepage') }}</title>
            <meta name="description" :content="$t('META-DESCRIPTIONS.homepage')" />
        </Head>

        <header class="header-search header-search--cruiser">
            <div class="header-search__background header-search__background--cruiser">
                <img class="header-search__background-video header-search__background-video--image"
                    src="@/assets/imgs/cruiser-background.jpg?quality=50" fetchpriority="high" loading="eager"
                    alt="cruiser" />
                <video class="header-search__background-video header-search__background-video--video"
                    src="@/assets/videos/nautyy.video.hp.small.webm" muted autoplay loop
                    poster="@/assets/imgs/cruiser-background.jpg?quality=50"></video>
            </div>

            <div class="main-search__container">
                <h1 class="main-search__heading-upper">
                    {{ $t('TEXTS.cruise-desktop-search-note') }}
                </h1>

                <Suspense>
                    <Lazy>
                        <MainSearchSection />
                    </Lazy>
                    <template #fallback>
                        <section class="main-search">
                            <header class="main-search__header">
                                <h1 class="main-search__header-heading main-search__header-heading--longer">
                                    {{ $t('TEXTS.find-journey-on-cruiser') }}
                                </h1>
                                <h1 class="main-search__header-heading main-search__header-heading--shorter">
                                    {{ $t('TEXTS.experience-cruise') }}
                                </h1>
                            </header>
                            <main class="main-search__main">
                                <SkeletonBox height="55px" />
                                <SkeletonBox height="55px" />
                                <SkeletonBox height="55px" />
                                <SkeletonBox height="55px" />
                                <SkeletonBox height="55px" />
                                <SkeletonBox height="55px" />
                            </main>
                        </section>
                    </template>
                </Suspense>

                <Suspense>
                    <Lazy>
                        <MainBannerLoader />
                    </Lazy>
                    <template #fallback>
                        <SkeletonBox height="100px" />
                    </template>
                </Suspense>
            </div>
        </header>
        <main class="main main--cruiser">
            <Suspense>
                <Lazy>
                    <DestinationsSection />
                </Lazy>
                <template #fallback>
                    <SkeletonBox height="400px" />
                </template>
            </Suspense>
            <LazyLoadOnVisible v-if="!PRODUCTION">
                <template #default>
                    <Suspense>
                        <RecommendedCruises />
                        <template #fallback>
                            <section class="section">
                                <div class="section__inner">
                                    <h2 class="section__heading section__heading--cruiser">
                                        {{ $t('LABELS.recommended-cruises') }}
                                    </h2>
                                    <div class="large-showcase">
                                        <ShowcaseItemSkeleton :style="{ display: 'flex', flexDirection: 'column' }" />
                                    </div>
                                </div>
                            </section>
                        </template>
                    </Suspense>
                </template>
                <template #fallback>
                    <SkeletonBox height="800px" />
                </template>
            </LazyLoadOnVisible>
            <LazyLoadOnVisible>
                <template #default>
                    <Suspense>
                        <DelegateCruiseSection />
                        <template #fallback>
                            <section class="section">
                                <div class="section__inner">
                                    <h2 class="section__heading section__heading--cruiser">
                                        {{ $t('LABELS.cruises-with-czech-delegate') }}
                                    </h2>
                                    <div class="large-showcase">
                                        <ShowcaseItemSkeleton :style="{ display: 'flex', flexDirection: 'column' }" />
                                    </div>

                                    <div class="btn--promo-wrap">
                                        <RouterLink to="/plavby-s-delegatem" class="btn btn--promo btn--blue">
                                            {{ $t('LABELS.show-all-delegate-cruises') }}
                                        </RouterLink>
                                    </div>
                                </div>
                            </section>
                        </template>
                    </Suspense>
                </template>
                <template #fallback>
                    <SkeletonBox height="830px" />
                </template>
            </LazyLoadOnVisible>

            <LazyLoadOnVisible>
                <template #default>
                    <Suspense>
                        <Reviews />
                        <template #fallback>
                            <SkeletonBox height="444px" />
                        </template>
                    </Suspense>
                </template>
                <template #fallback>
                    <SkeletonBox height="444px" />
                </template>
            </LazyLoadOnVisible>
        </main>
        <LazyLoadOnVisible>
            <Suspense>
                <Footer></Footer>
                <template #fallback>
                    <SkeletonBox height="200px" />
                </template>
            </Suspense>
            <template #fallback>
                <SkeletonBox height="200px" />
            </template>
        </LazyLoadOnVisible>
    </div>
</template>
<style lang="scss" scoped>
@import '@/assets/sass/layout/header-search.scss';
@import '@/assets/sass/components/main-search.scss';
</style>
